import { useRef, useState } from "react";
import { Mail, Phone, MessageCircle, Send, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import Reveal from "@/components/Reveal";

const INFO = [
    {
        icon: Mail,
        label: "E-mail",
        value: "ziggsforge@gmail.com",
        href: "https://mail.google.com/mail/?view=cm&fs=1&to=ziggsforge@gmail.com&su=Contato%20pelo%20site%20%E2%80%94%20Ziggs%20Forge&body=Ol%C3%A1!%20Vi%20o%20site%20da%20Ziggs%20Forge%20e%20quero%20falar%20sobre%20um%20projeto.",
        testid: "contact-email-link",
    },
    {
        icon: Phone,
        label: "Telefone",
        value: "+55 (22) 99998-9203",
        href: "tel:+5522999989203",
        testid: "contact-phone-link",
    },
    {
        icon: MessageCircle,
        label: "WhatsApp",
        value: "Chamar no WhatsApp",
        href: "https://wa.me/5522999989203",
        testid: "contact-whatsapp-link",
    },
];

const ACCEPTED_EXTS = ["jpg", "jpeg", "png", "pdf"];
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_TOTAL_BYTES = 15 * 1024 * 1024;
const MAX_FILES = 10;

const fmtSize = (b) =>
    b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(b / 1024))}KB`;

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [uploadStep, setUploadStep] = useState(0);
    const fileInput = useRef(null);

    const pickFiles = (e) => {
        const picked = Array.from(e.target.files || []);
        e.target.value = "";
        if (!picked.length) return;
        const next = [...files];
        for (const f of picked) {
            const ext = f.name.split(".").pop().toLowerCase();
            if (!ACCEPTED_EXTS.includes(ext)) {
                toast.error(`"${f.name}": formato não aceito. Use JPG, PNG ou PDF.`);
                continue;
            }
            if (f.size > MAX_FILE_BYTES) {
                toast.error(`"${f.name}" passa de 10MB.`);
                continue;
            }
            if (next.length >= MAX_FILES) {
                toast.error("Máximo de 10 anexos por mensagem.");
                break;
            }
            if (next.reduce((s, x) => s + x.size, 0) + f.size > MAX_TOTAL_BYTES) {
                toast.error("Os anexos passariam de 15MB no total.");
                continue;
            }
            if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
            next.push(f);
        }
        setFiles(next);
    };

    const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const attachments = [];
            for (let i = 0; i < files.length; i++) {
                setUploadStep(i + 1);
                const fd = new FormData();
                fd.append("file", files[i]);
                const { data } = await api.post("/contact/upload", fd);
                attachments.push(data);
            }
            await api.post("/contact", { ...form, attachments });
            toast.success("Mensagem enviada! Confirmação a caminho do seu e-mail.");
            setForm({ name: "", email: "", message: "" });
            setFiles([]);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSending(false);
            setUploadStep(0);
        }
    };

    return (
        <section className="pb-24 pt-28 md:pb-32 md:pt-40" data-testid="contact-page">
            <div className="zf-container">
                <Reveal>
                    <p className="zf-eyebrow">Contato</p>
                    <h1 className="mt-4 font-display text-4xl font-black uppercase leading-tight text-forge-paper md:text-6xl">
                        Fale com <br className="hidden md:block" />
                        a <span className="text-forge-yellow">forja</span>
                    </h1>
                    <p className="mt-4 max-w-lg text-forge-dim">
                        Orçamento, dúvida ou ideia maluca? Manda mensagem — a gente responde rápido e sem robô.
                    </p>
                </Reveal>

                <div className="mt-14 grid gap-12 lg:grid-cols-[1fr_1.2fr]">
                    <div className="space-y-4">
                        {INFO.map((item, i) => (
                            <Reveal key={item.label} delay={0.06 * i}>
                                <a
                                    href={item.href}
                                    target={item.href.startsWith("http") ? "_blank" : undefined}
                                    rel="noreferrer"
                                    data-testid={item.testid}
                                    className="group flex items-center gap-5 border border-forge-line bg-forge-panel p-6 transition-colors duration-300 hover:border-forge-yellow/60"
                                >
                                    <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-forge-yellow/40 text-forge-yellow transition-colors duration-300 group-hover:bg-forge-yellow group-hover:text-forge-ink">
                                        <item.icon className="h-5 w-5" />
                                    </span>
                                    <span>
                                        <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-forge-dim">
                                            {item.label}
                                        </span>
                                        <span className="mt-1 block font-medium text-forge-paper">{item.value}</span>
                                    </span>
                                </a>
                            </Reveal>
                        ))}
                        <Reveal delay={0.2}>
                            <div className="layer-lines border border-forge-line bg-forge-panel/40 p-6">
                                <p className="font-display text-sm font-bold uppercase text-forge-yellow">
                                    Prefere pedir algo personalizado?
                                </p>
                                <p className="mt-2 text-sm leading-relaxed text-forge-dim">
                                    Descreva o que você imaginou no formulário — pode mandar referências, medidas e
                                    cores com fotos (JPG ou PNG) e PDFs anexados. A gente transforma em orçamento.
                                </p>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.1}>
                        <form
                            onSubmit={submit}
                            className="layer-lines border border-forge-line bg-forge-panel p-8 md:p-10"
                            data-testid="contact-form"
                        >
                            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-forge-yellow">
                                Envie sua mensagem
                            </p>
                            <div className="mt-6 space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="contact-name" className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim">
                                        Nome
                                    </Label>
                                    <Input
                                        id="contact-name"
                                        data-testid="contact-name-input"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        required
                                        placeholder="Seu nome"
                                        className="border-forge-line bg-forge-ink focus-visible:ring-forge-yellow"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="contact-email" className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim">
                                        E-mail
                                    </Label>
                                    <Input
                                        id="contact-email"
                                        type="email"
                                        data-testid="contact-email-input"
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        required
                                        placeholder="voce@email.com"
                                        className="border-forge-line bg-forge-ink focus-visible:ring-forge-yellow"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="contact-message" className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim">
                                        Mensagem
                                    </Label>
                                    <Textarea
                                        id="contact-message"
                                        data-testid="contact-message-input"
                                        value={form.message}
                                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                                        required
                                        placeholder="Conte o que você quer imprimir..."
                                        className="min-h-[140px] border-forge-line bg-forge-ink focus-visible:ring-forge-yellow"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <input
                                        ref={fileInput}
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.pdf"
                                        multiple
                                        hidden
                                        data-testid="contact-attach-input"
                                        onChange={pickFiles}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileInput.current?.click()}
                                        disabled={sending}
                                        data-testid="contact-attach-button"
                                        className="flex w-full items-center justify-center gap-2 border border-dashed border-forge-line bg-forge-ink/60 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim transition-colors duration-300 hover:border-forge-yellow/60 hover:text-forge-yellow disabled:opacity-60"
                                    >
                                        <Paperclip className="h-4 w-4" />
                                        Anexar arquivos (JPG, PNG ou PDF)
                                    </button>
                                    {files.length > 0 && (
                                        <ul className="space-y-1" data-testid="contact-file-list">
                                            {files.map((f, i) => (
                                                <li
                                                    key={`${f.name}-${i}`}
                                                    data-testid={`contact-file-item-${i}`}
                                                    className="flex items-center justify-between gap-3 border border-forge-line bg-forge-ink px-3 py-2 text-xs text-forge-paper"
                                                >
                                                    <span className="truncate">
                                                        {f.name} <span className="text-forge-dim">({fmtSize(f.size)})</span>
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFile(i)}
                                                        data-testid={`contact-file-remove-${i}`}
                                                        aria-label={`Remover ${f.name}`}
                                                        className="shrink-0 text-forge-dim transition-colors duration-300 hover:text-forge-yellow"
                                                    >
                                                        <X className="h-3.5 w-3.5" />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <Button
                                    type="submit"
                                    data-testid="contact-submit"
                                    disabled={sending}
                                    className="w-full bg-forge-yellow font-mono text-xs uppercase tracking-[0.2em] text-forge-ink hover:bg-forge-yellow/90 md:w-auto md:px-10"
                                >
                                    {sending
                                        ? uploadStep
                                            ? `Enviando anexos (${uploadStep}/${files.length})...`
                                            : "Enviando..."
                                        : "Enviar mensagem"}
                                    <Send className="h-4 w-4" />
                                </Button>
                            </div>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
